/**
 * @Author: Zero
 * @Date: 2024-10-23 18:58:48
 * @Explain: 级联自定义 - 下拉框
 */

import { Icon, Input, Link, Tooltip } from '@/components/tdesign';
import { intlGet } from '@/locales';
import classNames from 'classnames';
import { useMemo, useState } from 'react';
import {
  InputProps,
  Loading,
  Tag,
  TagInput,
  TagInputProps,
} from 'tdesign-react';
// utils
import {
  isArray,
  isArrayLen,
  isEffectiveValue,
  isObject,
} from '@/utils/common/dataJudgment';
import { minusBy } from '@/utils/format/number';
import { cascaderInterval } from '../const';
// type
import { CascaderSelectProps, SelectBseProps } from '../data.d';

type PropsType = {
  valueText?: any;
  visible?: boolean;
  searchPlaceholder?: string;
  /** 自动聚焦 */
  autofocus?: boolean;
  onSearch?: (val: string) => void;
  onTagRemove?: TagInputProps['onRemove'];
  /** 是否是级联选择器使用 */
  isCascader?: boolean;
  /** 完整路径名称 */
  fullPathName?: CascaderSelectProps['fullPathName'];
  // 选中结果框渲染key
  displaySelectedResTextKey?: CascaderSelectProps['displaySelectedResTextKey'];
} & SelectBseProps;

function CascaderCustom(props: PropsType) {
  const {
    className,
    visible,
    valueText,
    value,
    placeholder = intlGet('COMMON.PLEASE.SELECT'),
    loading,
    disabled,
    readonly = true,
    clearable,
    multiple,
    size,
    borderless = false,
    autoWidth,
    displayText,
    displayTextKey,
    displayTextType = 'text',
    prefixIcon,
    suffixIconName = 'select-down',
    // tag - input - props
    minCollapsedNum,
    collapsedItems,
    valueDisplay,
    collapsedToolTipProps,
    searchPlaceholder,
    autofocus = false,
    isCascader = false,
    fullPathName,
    displaySelectedResTextKey,
    onSearch,
    onClick,
    onClearable,
    onTagRemove,
  } = props;

  // displayTextType为'autoComplete'时搜索框内容
  const [inputSearchValue, setInputSearchValue] = useState<string>(value);

  const isAutoComplete = useMemo(
    () => displayTextType === 'autoComplete',
    [displayTextType],
  );

  /** 标签式 展示 */
  const renderCollapsedItems = ({ collapsedTags }: any) => (
    <Tooltip
      overlayClassName="select-tooltip-collapsed-tag"
      theme="light"
      placementOffset={[5, 0]}
      content={collapsedTags.map((item: any) => (
        <Tag size="large" key={item} className="mb-8 mr-8">
          {item}
        </Tag>
      ))}
      {...collapsedToolTipProps}
    >
      <Tag size="large">+{collapsedTags?.length}</Tag>
    </Tooltip>
  );

  // 文本展示 - map
  const displayTextMap = useMemo(() => {
    let result: any[] = [];
    if (isArray(valueText)) {
      for (let i = 0; i < valueText.length; i++) {
        let item = valueText[i];
        // 选中结果框显示完整路径名称
        if (fullPathName) {
          let res = '';
          item?.map((v: any, index: number) => {
            let val = v?.label;
            if (displaySelectedResTextKey && v[displaySelectedResTextKey]) {
              val = v[displaySelectedResTextKey];
            }
            res += `${val}${
              index !== minusBy(item.length, 1) ? cascaderInterval : ''
            }`;
          });
          result.push(res);
        }
        // 选中结果框显示路径最底层名称
        else {
          let obj = item?.[minusBy(item.length, 1)] || {};
          let val = obj?.label;
          if (displaySelectedResTextKey && obj?.[displaySelectedResTextKey]) {
            val = obj?.[displaySelectedResTextKey];
          }
          result.push(val);
        }
      }
    }
    return result;
  }, [valueText, displaySelectedResTextKey, fullPathName]);

  // 根据不同的情况，是否开启 display-text 样式
  const displayTextClass = useMemo(() => {
    if (isAutoComplete) {
      return false;
    } else if (isArray(value)) {
      if (isArrayLen(value)) {
        return true;
      }
    } else if (value) {
      if (displayTextType === 'customText' || autoWidth) {
        return true;
      }
    }
    return false;
  }, [value, displayTextType]);

  // 公共配置
  const commonProps = useMemo(() => {
    let config: InputProps = {
      className: classNames(
        'default-td-select-box',
        {
          borderless,
          'is-focused': visible,
          't-tag-input': multiple,
          'display-text': displayTextClass,
          [`size-${size}`]: !autoWidth && size,
        },
        className,
      ),
      readonly: isAutoComplete ? false : !displayTextClass,
      disabled,
      autoWidth,
      clearable,
      placeholder: isAutoComplete ? searchPlaceholder : placeholder,
      suffixIcon: isAutoComplete ? undefined : loading ? (
        <Loading size="small" />
      ) : (
        <Link className="link-icon">
          <Icon.Font name={suffixIconName} fontSize={24} />
        </Link>
      ),
      onClick,
      onClear: ({ e }) => {
        onClearable?.();
        e.stopPropagation();
      },
    };

    if (prefixIcon) {
      config.prefixIcon = prefixIcon;
    }

    return config as any;
  }, [
    className,
    displayTextClass,
    visible,
    value,
    size,
    multiple,
    readonly,
    disabled,
    loading,
    clearable,
    autoWidth,
    displayTextKey,
    prefixIcon,
    suffixIconName,
    isAutoComplete,
  ]);

  const inputProps = useMemo(() => {
    let config: InputProps & { title: string } = {
      autofocus,
      value: isAutoComplete ? inputSearchValue : value || '',
      title: valueText?.[0]?.label || displayText,
      onChange: (val) => {
        setInputSearchValue(val);
        onSearch?.(val);
      },
    };
    let labelText: any = displayText;

    if (displayTextType !== 'displayText') {
      if (
        isEffectiveValue(value) &&
        displayTextMap &&
        isArrayLen(displayTextMap)
      ) {
        labelText = displayTextMap[0];
      }
    }
    if (labelText) {
      if (isObject(labelText) || autoWidth || displayTextClass) {
        config.prefixIcon = labelText;
      } else {
        config.value = labelText;
      }
    }

    return config;
  }, [
    valueText,
    displayTextClass,
    value,
    displayTextMap,
    autoWidth,
    displayText,
    displayTextKey,
    displayTextType,
    inputSearchValue,
    autofocus,
    isAutoComplete,
  ]);

  const tagInputProps = useMemo(() => {
    let config: TagInputProps = {
      value: value || [],
      size: 'large',
      minCollapsedNum,
      collapsedItems: collapsedItems || renderCollapsedItems,
      valueDisplay,
      onRemove: (context) => {
        onTagRemove?.(context);
        context.e?.stopPropagation();
      },
    };
    let labelText: TagInputProps['value'] = [];

    if (isArrayLen(value) && displayTextMap && isArrayLen(displayTextMap)) {
      labelText = displayTextMap;
    }

    config.value = labelText;
    return config;
  }, [
    value,
    displayTextMap,
    minCollapsedNum,
    collapsedItems,
    valueDisplay,
    isCascader,
    renderCollapsedItems,
    onTagRemove,
  ]);

  return displayTextType === 'tag' || multiple ? (
    <TagInput {...tagInputProps} {...commonProps} />
  ) : isAutoComplete ? (
    <Input {...inputProps} {...commonProps} autofocus />
  ) : (
    <Input {...inputProps} {...commonProps} />
  );
}

export default CascaderCustom;
